import React from 'react'
import { Dropdown } from 'react-bootstrap'
import eqxApiInstance from '../../classes/eqxApi'
import eqxEndpoint from '../../classes/eqxEndpoint'
import { EqxFormInputHidden, EqxFormInputText, EqxFormRow, useFormState } from '../../components/EqxForm/EqxForm'
import EqxFormModal, { useDerivedModalState } from '../../components/EqxFormModal'
import useApiEndpoint from '../../hooks/ApiEndpoint'
import formatBytes from '../../utils/formatBytes'
import truncate from '../../utils/truncate'
import EqxButton from '../EqxButton/EqxButton'
import Card, { EqxCardEmpty as CardEmpty } from '../EqxCard/EqxCard'
import EqxModal from '../EqxModal/EqxModal'
import EqxRadioButton, { EqxRadioButtonItem } from '../EqxRadioButton'
import RequestHandler from '../EqxRequestHandler/EqxRequestHandler'
import EqxUploadPanel from '../EqxUploadPanel/EqxUploadPanel'
import './EqxDocumentsPanel.css'

export interface eqxDocumentModel {
    isVideo: boolean
    previewImageUrl: string
    url: string
    isImage: boolean
    displayName: string
    id: number
    friendlyName: string
    originalName: string
    extension: string
    fontAwesomeIconName: string
    contentLength: number
    typeName: string
}
export interface eqxDocumentDeleteInput {
    documentId: number
}
export default (props: {
    api: eqxApiInstance
    endpointIndex: eqxEndpoint<eqxDocumentModel[]>
    endpointEdit?: eqxEndpoint<any, FormData>
    endpointUpload?: eqxEndpoint<any, FormData>
    endpointDelete?: eqxEndpoint<any, eqxDocumentDeleteInput>
    id?: string
    readOnly?: boolean
    show?: boolean
    isSection?: boolean
    showPreviews?: boolean
    disableDownloads?: boolean
    messages?: {
        addSuccessMessage: (message: string, key?: string, autoHide?: boolean) => void
        addErrorMessage: (message: string, key?: string, autoHide?: boolean) => void
        addLoadingMessage: (message: string, key?: string, autoHide?: boolean) => void
    }
}) => {
    const [show, setShow] = React.useState(props.show ?? true)
    const [isUploadingOpen, setIsUploadingOpen] = React.useState(false)
    const [view, setView] = React.useState<string>('grid')
    const [editingDocument, setEditingDocument] = React.useState<eqxDocumentModel | null>(null)
    const [viewingDocument, setViewingDocument] = React.useState<eqxDocumentModel | null>(null)

    const id: string = props.id ?? (Math.random() + 1).toString(36).substring(7)
    const readOnly = props.readOnly ?? false
    const isInModal = false
    const showPreviews = props.showPreviews ?? true
    const disableDownloads = props.disableDownloads ?? false
    const docsEndpoint = props.endpointIndex
    const editEndpoint = props.endpointEdit
    const uploadEndpoint = props.endpointUpload
    const deleteEndpoint = props.endpointDelete
    if (!readOnly && !uploadEndpoint) {
        console.error('uploadEndpoint is required when readOnly is false')
    }
    if (!readOnly && !deleteEndpoint) {
        console.error('deleteEndpoint is required when readOnly is false')
    }
    if (!readOnly && !editEndpoint) {
        console.error('deleteEndpoint is required when readOnly is false')
    }
    //get messages from parameter, if not just > /dev/null
    const messages = props.messages ?? {
        addSuccessMessage: (message: string, key?: string, autoHide?: boolean) => console.log(message),
        addErrorMessage: (message: string, key?: string, autoHide?: boolean) => console.error(message),
        addLoadingMessage: (message: string, key?: string, autoHide?: boolean) => console.log(message),
    }

    const api = props.api
    const {
        addSuccessMessage: showGlobalSuccess,
        addErrorMessage: showGlobalError,
        addLoadingMessage: showGlobalLoading,
    } = messages

    const {
        status,
        loading,
        error,
        response: documents,
        reset: resetDocuments,
    } = useApiEndpoint<eqxDocumentModel[]>(api, docsEndpoint, show)
    const onViewChange = function (event: React.ChangeEvent<HTMLInputElement>) {
        let val = event.target.value
        setView(val == 'grid' ? 'grid' : 'list')
    }
    const onUploadSuccess = function (documents: eqxDocumentModel[]) {
        showGlobalSuccess('Document(s) uploaded successfully')
        resetDocuments()
    }
    const editHandler = (document: eqxDocumentModel) => (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()
        setEditingDocument(document)
    }
    const deleteHandler = function (event: React.MouseEvent<HTMLAnchorElement>, id: number) {
        event.preventDefault()
        const endpoint = deleteEndpoint
        if (!endpoint) return
        showGlobalLoading('Deleting Document...', 'document-delete-' + id)
        if (window.confirm('Are you sure you want to delete this document?')) {
            endpoint
                .execute(api, { documentId: id })
                .then(() => {
                    showGlobalSuccess('Document deleted successfully', 'document-delete-' + id)
                    resetDocuments()
                })
                .catch(() =>
                    showGlobalError(
                        'An error occurred while deleting the document. Please try again.',
                        'document-delete-' + id
                    )
                )
        }
    }
    const viewHandler = (document: eqxDocumentModel) => (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()
        setViewingDocument(document)
    }
    const displayDocument = (document: eqxDocumentModel) => {
        let downloadUrl: string = ''
        let popupUrl: string = ''
        let imageUrl: string = ''

        downloadUrl = document.url
        popupUrl = document.isImage ? document.previewImageUrl : downloadUrl
        imageUrl = document.previewImageUrl

        const documentOptions = (document: eqxDocumentModel) => (
            <Dropdown drop="up" align="end">
                <Dropdown.Toggle
                    variant="outline-secondary"
                    size="sm"
                    id={`dropdown-${document.id}`}
                    title="Options"
                >
                    <span className="fas fa-ellipsis-h"></span>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                    <Dropdown.Item as="span">
                        <strong>{truncate(document.originalName, 20)}</strong>
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item
                        href={disableDownloads ? undefined : downloadUrl}
                        download={disableDownloads ? undefined : downloadUrl}
                        className={`btn-download offline-disabled ${disableDownloads ? 'text-muted' : ''}`}
                        disabled={disableDownloads}
                    >
                        <span className="fas fa-download fa-fw text-primary"></span> Download
                    </Dropdown.Item>
                    {!readOnly && (
                        <>
                            {!isInModal && (
                                <Dropdown.Item
                                    href="#"
                                    onClick={editHandler(document)}
                                    className="edit-document"
                                >
                                    <span className="fas fa-pencil-alt fa-fw text-warning"></span> Edit
                                </Dropdown.Item>
                            )}
                            <Dropdown.Item
                                href="#"
                                onClick={(event: React.MouseEvent) => deleteHandler(event as any, document.id)}
                                className="delete-document"
                            >
                                <span className="fas fa-times fa-fw text-danger"></span> Remove
                            </Dropdown.Item>
                        </>
                    )}
                </Dropdown.Menu>
            </Dropdown>
        )
        const displayDocumentList = (document: eqxDocumentModel) => {
            return (
                <tr
                    className={
                        'document document-' +
                        document.extension +
                        (document.isVideo ? ' video' : document.isImage ? ' image' : '')
                    }
                    key={document.id}
                >
                    <td>
                        <span className={'fas fa-' + document.fontAwesomeIconName + ' fs-2 text-secondary'}></span>
                    </td>
                    <td>
                        <strong className="text-truncate">
                            <a className={`offline-disabled ${disableDownloads ? 'text-muted' : ''}`}
                                onClick={disableDownloads ? undefined : viewHandler(document)}>
                                {document.originalName}
                            </a>
                        </strong>
                        {document.friendlyName && (
                            <>
                                <br />
                                <em className="text-muted">{document.friendlyName}</em>
                            </>
                        )}
                    </td>
                    <td>{document.typeName}</td>
                    <td data-sort={document.contentLength}>{formatBytes(document.contentLength)}</td>
                    <td className="text-end">{documentOptions(document)}</td>
                </tr>
            )
        }
        const displayDocumentGrid = (document: eqxDocumentModel) => {
            return (
                <div
                    className={
                        'col-6 col-md-4 col-lg-3 col-xl-2 eqx-document eqx-document-' +
                        document.extension +
                        (document.isVideo ? ' video' : document.isImage ? ' image' : '')
                    }
                    key={document.id}
                >
                    <div className="eqx-document-box">
                        {document.isImage && showPreviews && (
                            <div className="responsive-square mx-auto" style={{ maxWidth: 200 }}>
                                <a className={`offline-disabled ${disableDownloads ? 'text-muted' : ''}`}
                                    onClick={disableDownloads ? undefined : viewHandler(document)}>
                                    <img
                                        src={imageUrl}
                                        className="responsive-square-content img-responsive center-block"
                                        title={document.displayName}
                                    />
                                </a>
                            </div>
                        )}
                        {(!document.isImage || !showPreviews) && (
                            <div className="responsive-square mx-auto" style={{ maxWidth: 200 }}>
                                <span className={'fas fa-' + document.fontAwesomeIconName + ' eqx-document-icon text-secondary'}></span>
                            </div>
                        )}
                        <div
                            className="eqx-document-caption text-truncate"
                            title={document.displayName}
                            data-toggle="tooltip"
                            data-bs-toggle="tooltip"
                        >
                            {truncate(document.displayName, 80)}
                        </div>
                        <div className="eqx-document-options">{documentOptions(document)}</div>
                    </div>
                </div>
            )
        }
        return view == 'grid' ? displayDocumentGrid(document) : displayDocumentList(document)
    }
    const ViewDocumentModal = (props: { document: eqxDocumentModel }) => {
        const document = props.document
        const editModal = useDerivedModalState({ shown: true, onHide: () => setViewingDocument(null) })
        if (!editEndpoint) {
            console.error('editEndpoint is required')
            return <></>
        }
        return (
            <EqxModal
                state={editModal}
                title={document.displayName}
                footer={
                    <>
                        <EqxButton variant="secondary" onClick={() => setViewingDocument(null)}>
                            Close
                        </EqxButton>
                        <EqxButton variant="primary" href={document.url} download={document.originalName}>
                            Download
                        </EqxButton>
                    </>
                }
            >
                <div className="text-center">
                    {document.isImage ? (
                        <img src={document.url} className="w-100" />
                    ) : document.isVideo ? (
                        <video src={document.url} controls className="w-100"></video>
                    ) : (
                        <div className="text-center">
                            <span className={'fas fa-' + document.fontAwesomeIconName + ' eqx-document-icon text-secondary'}></span>
                        </div>
                    )}
                </div>
            </EqxModal>
        )
    }
    const EditDocumentModal = (props: { document: eqxDocumentModel }) => {
        const document = props.document
        const editForm = useFormState(undefined, { data: { friendlyName: document.friendlyName, documentId: document.id } })
        const editModal = useDerivedModalState({ shown: true, onHide: () => setEditingDocument(null) })
        if (!editEndpoint) {
            console.error('editEndpoint is required')
            return <></>
        }
        const handleEditSuccess = () => {
            resetDocuments()
            setEditingDocument(null)
        }
        return (
            <EqxFormModal
                api={api}
                title="Edit Document"
                action={editEndpoint.url}
                method={editEndpoint.method}
                modal={editModal}
                form={editForm}
                onSuccess={handleEditSuccess}
            >
                <EqxFormInputHidden name="documentId" />
                <EqxFormRow name="originalName" label="File Name">
                    {document.originalName}
                </EqxFormRow>
                <EqxFormRow name="friendlyName" label="Friendly Name">
                    <EqxFormInputText name="friendlyName" />
                </EqxFormRow>
            </EqxFormModal>
        )
    }
    return (
        <>
            {editingDocument && <EditDocumentModal document={editingDocument} />}
            {viewingDocument && <ViewDocumentModal document={viewingDocument} />}
            <Card
                isSection={props.isSection ?? false}
                title="Documents"
                noPadding={true}
                collapsible={true}
                show={show}
                setShow={setShow}
                headerRight={
                    <>
                        {show && (
                            <>
                                <EqxRadioButton
                                    name="documents-view"
                                    value={view}
                                    variant="primary"
                                    displayIcon="d-none d-sm-inline"
                                    onChange={onViewChange}
                                >
                                    <EqxRadioButtonItem value="grid" label="Grid View" icon="fas fa-th-large" />
                                    <EqxRadioButtonItem value="list" label="List View" icon="fas fa-list" />
                                </EqxRadioButton>
                            </>
                        )}
                        {!readOnly && (
                            <div className="btn-group ms-2" role="group">
                                <a
                                    href="#"
                                    className="btn btn-success btn-sm"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        setShow(true)
                                        setIsUploadingOpen(!isUploadingOpen)
                                    }}
                                >
                                    <span className="fas fa-upload"></span>{' '}
                                    <span className="d-none d-md-inline">Add Document</span>
                                </a>
                            </div>
                        )}
                    </>
                }
            >
                {isUploadingOpen && uploadEndpoint && (
                    <div className="p-3 pb-0">
                        <EqxUploadPanel endpoint={uploadEndpoint.url} onSuccess={onUploadSuccess} />
                    </div>
                )}
                <RequestHandler
                    state={{ loading: loading, error: error }}
                    isEmpty={documents != null && documents.length == 0}
                    empty={<CardEmpty>No documents found</CardEmpty>}
                >
                    {view == 'list' ? (
                        <div className="eqx-document-panel-table-container table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Size</th>
                                        <th className="text-end">Options</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documents?.map((document: eqxDocumentModel) => {
                                        return displayDocument(document)
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="eqx-document-panel-grid-container ">
                            <div className="row equal-height">
                                {documents?.map((document: eqxDocumentModel) => {
                                    return displayDocument(document)
                                })}
                            </div>
                        </div>
                    )}
                </RequestHandler>
            </Card>
        </>
    )
}
